import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Clock, Building2, Car, GraduationCap, Home, DollarSign, Shield,
  ChevronDown, ChevronUp, RefreshCw, Loader2, FileText,
  AlertCircle, Info, Calendar, Briefcase, MapPin, Phone, Mail, User,
  BarChart3
} from 'lucide-react';
import { fetchWithAuth, BASE_URL } from '../../api/client';
import BureauReportSection from './BureauReportView';

// Score color helper
const getScoreColor = (score) => {
  if (score >= 740) return { bg: 'bg-emerald-500', text: 'text-emerald-500', label: 'Excellent' };
  if (score >= 670) return { bg: 'bg-sky-500', text: 'text-sky-500', label: 'Good' };
  if (score >= 580) return { bg: 'bg-amber-500', text: 'text-amber-500', label: 'Fair' };
  return { bg: 'bg-red-500', text: 'text-red-500', label: 'Poor' };
};

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === 'N/A' || amount === '') return 'N/A';
  const num = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]/g, '')) : amount;
  if (isNaN(num)) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(num);
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `${parseFloat(value).toFixed(1)}%`;
};

// Credit Score Card Component
const CreditScoreCard = ({ bureau, score, scoreModel }) => {
  const scoreNum = parseInt(score) || 0;
  const scoreInfo = getScoreColor(scoreNum);

  return (
    <div className="dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200 rounded-xl p-4 flex flex-col items-center">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{bureau}</p>
      <div className="relative w-24 h-24 mb-2">
        <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 36 36">
          <path
            className="dark:stroke-slate-700 stroke-slate-200"
            strokeWidth="3"
            fill="none"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          />
          <path
            className={scoreInfo.text}
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
            strokeDasharray={`${(scoreNum / 850) * 100}, 100`}
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            style={{ stroke: 'currentColor' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-2xl font-bold ${scoreInfo.text}`}>{scoreNum}</span>
          <span className="text-[10px] text-slate-500">{scoreModel || 'FICO'}</span>
        </div>
      </div>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${scoreInfo.bg} text-white`}>
        {scoreInfo.label}
      </span>
    </div>
  );
};

// Score Summary Section - Uses summary.credit_scores
const ScoreSummarySection = ({ summary }) => {
  const scores = summary?.credit_scores || {};

  // Field names from API: experian_fico_2, equifax_fico_5, transunion_fico_4
  const experianScore = scores.experian_fico_2 || scores.experian || scores.EX || 0;
  const equifaxScore = scores.equifax_fico_5 || scores.equifax || scores.EQ || 0;
  const transunionScore = scores.transunion_fico_4 || scores.transunion || scores.TU || 0;
  const middleScore = scores.middle_score || scores.middle || scores.average ||
    Math.round((parseInt(experianScore) + parseInt(equifaxScore) + parseInt(transunionScore)) / 3) || 0;

  const middleScoreInfo = getScoreColor(middleScore);

  // Risk and positive factors from summary
  const riskFactors = summary?.risk_factors || [];
  const positiveFactors = summary?.positive_factors || [];

  return (
    <div className="space-y-6">
      {/* Credit Scores Card */}
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 dark:text-sky-400 text-sky-600" />
            Credit Score Summary
          </h3>
          <div className={`px-3 py-1 rounded-full ${middleScoreInfo.bg} text-white text-sm font-semibold`}>
            Middle Score: {middleScore}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <CreditScoreCard bureau="Experian" score={experianScore} scoreModel="FICO 2" />
          <CreditScoreCard bureau="Equifax" score={equifaxScore} scoreModel="FICO 5" />
          <CreditScoreCard bureau="TransUnion" score={transunionScore} scoreModel="FICO 4" />
        </div>

        {/* Score Factors */}
        {(riskFactors.length > 0 || positiveFactors.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {riskFactors.length > 0 && (
              <div className="dark:bg-amber-500/10 bg-amber-50 rounded-xl p-4">
                <p className="text-sm font-medium dark:text-amber-300 text-amber-800 mb-2 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4" />
                  Risk Factors
                </p>
                <div className="space-y-1">
                  {riskFactors.map((factor, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs dark:text-amber-200 text-amber-700">
                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{factor}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {positiveFactors.length > 0 && (
              <div className="dark:bg-emerald-500/10 bg-emerald-50 rounded-xl p-4">
                <p className="text-sm font-medium dark:text-emerald-300 text-emerald-800 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Positive Factors
                </p>
                <div className="space-y-1">
                  {positiveFactors.map((factor, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs dark:text-emerald-200 text-emerald-700">
                      <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{factor}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Debt Summary Section - Uses summary.total_debt and summary.credit_utilization
const DebtSummarySection = ({ summary }) => {
  const totalDebt = summary?.total_debt || {};
  const utilization = summary?.credit_utilization || {};
  const paymentHistory = summary?.payment_history || {};

  return (
    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
      <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
        <DollarSign className="w-5 h-5 dark:text-sky-400 text-sky-600" />
        Debt Summary
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50">
          <p className="text-xs text-slate-500 mb-1">Revolving Debt</p>
          <p className="text-xl font-bold dark:text-white text-slate-900">{formatCurrency(totalDebt.revolving)}</p>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50">
          <p className="text-xs text-slate-500 mb-1">Installment Debt</p>
          <p className="text-xl font-bold dark:text-white text-slate-900">{formatCurrency(totalDebt.installment)}</p>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50">
          <p className="text-xs text-slate-500 mb-1">Mortgage Debt</p>
          <p className="text-xl font-bold dark:text-white text-slate-900">{formatCurrency(totalDebt.mortgage)}</p>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50">
          <p className="text-xs text-slate-500 mb-1">Total Debt</p>
          <p className="text-xl font-bold dark:text-sky-400 text-sky-600">{formatCurrency(totalDebt.total)}</p>
        </div>
      </div>

      {/* Credit Utilization */}
      {(utilization.utilization_percentage !== undefined || utilization.percentage !== undefined) && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">Credit Utilization</span>
            <span className={`text-sm font-semibold ${
              (utilization.utilization_percentage || utilization.percentage || 0) > 30 ? 'text-amber-500' : 'text-emerald-500'
            }`}>
              {formatPercent(utilization.utilization_percentage || utilization.percentage)}
            </span>
          </div>
          <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                (utilization.utilization_percentage || utilization.percentage || 0) > 50 ? 'bg-red-500' :
                (utilization.utilization_percentage || utilization.percentage || 0) > 30 ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(utilization.utilization_percentage || utilization.percentage || 0, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>Balance: {formatCurrency(utilization.total_revolving_balance)}</span>
            <span>Total Limit: {formatCurrency(utilization.total_credit_limit)}</span>
          </div>
        </div>
      )}

      {/* Payment History */}
      {paymentHistory && (
        <div className="dark:bg-slate-800/50 bg-slate-50 rounded-xl p-4">
          <p className="text-sm font-medium dark:text-white text-slate-900 mb-3">Payment History</p>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-emerald-500">{paymentHistory.on_time || paymentHistory.total_on_time || 0}</p>
              <p className="text-xs text-slate-500">On Time</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-500">{paymentHistory.late_30 || paymentHistory.total_30_day_late || 0}</p>
              <p className="text-xs text-slate-500">30-Day Late</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-500">{paymentHistory.late_60 || paymentHistory.total_60_day_late || 0}</p>
              <p className="text-xs text-slate-500">60-Day Late</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-500">{paymentHistory.late_90 || paymentHistory.total_90_day_late || 0}</p>
              <p className="text-xs text-slate-500">90+ Day Late</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Trade Account Row Component
const TradeAccountRow = ({ account, isExpanded, onToggle }) => {
  const getAccountIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('mortgage') || t.includes('home') || t.includes('real estate')) return Home;
    if (t.includes('auto') || t.includes('car') || t.includes('vehicle')) return Car;
    if (t.includes('student') || t.includes('education')) return GraduationCap;
    return CreditCard;
  };

  // Handle different field name conventions
  const accountType = account.account_type || account.type || account.loan_type || 'Account';
  const company = account.company || account.creditor || account.creditor_name || account.name || 'Unknown';
  const accountNumber = account.account_number || account.acct_number || account.number || '';
  const balance = account.balance || account.current_balance || account.unpaid_balance || 0;
  const creditLimit = account.credit_limit || account.high_credit || account.original_amount || null;
  const monthlyPayment = account.monthly_payment_amount || account.monthly_payment || account.payment || 0;
  const accountStatus = account.account_status || account.status || 'Unknown';
  const accountRating = account.account_rating || account.rating || account.pay_status || '';
  const dateOpened = account.date_of_opening || account.date_opened || account.open_date || '';
  const lastPaymentDate = account.last_payment_date || account.last_payment || '';

  const Icon = getAccountIcon(accountType);
  const isGoodStanding = accountRating?.toLowerCase().includes('good') ||
                         accountRating?.toLowerCase().includes('agreed') ||
                         accountRating?.toLowerCase().includes('current') ||
                         accountRating?.toLowerCase().includes('as agreed');

  const utilization = creditLimit && parseFloat(creditLimit) > 0
    ? (parseFloat(balance) / parseFloat(creditLimit)) * 100
    : null;

  return (
    <div className="border-b dark:border-slate-700 border-slate-200 last:border-0">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left"
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isGoodStanding ? 'dark:bg-emerald-500/20 bg-emerald-100' : 'dark:bg-amber-500/20 bg-amber-100'}`}>
          <Icon className={`w-5 h-5 ${isGoodStanding ? 'text-emerald-500' : 'text-amber-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium dark:text-white text-slate-900 truncate">{company}</p>
            {isGoodStanding ? (
              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-slate-500">{accountType} {accountNumber ? `• ${accountNumber}` : ''}</p>
        </div>
        <div className="text-right">
          <p className="font-semibold dark:text-white text-slate-900">{formatCurrency(balance)}</p>
          <p className="text-xs text-slate-500">
            {creditLimit ? `of ${formatCurrency(creditLimit)}` : monthlyPayment ? `${formatCurrency(monthlyPayment)}/mo` : ''}
          </p>
        </div>
        <div className="w-20 text-right">
          <p className="text-sm font-medium dark:text-white text-slate-900">{formatCurrency(monthlyPayment)}</p>
          <p className="text-xs text-slate-500">Payment</p>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-2 dark:bg-slate-800/30 bg-slate-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-500">Account Status</p>
              <p className={`font-medium ${accountStatus?.toLowerCase() === 'open' ? 'text-emerald-500' : 'text-slate-400'}`}>
                {accountStatus}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Account Rating</p>
              <p className={`font-medium ${isGoodStanding ? 'text-emerald-500' : 'text-amber-500'}`}>
                {accountRating || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Date Opened</p>
              <p className="dark:text-white text-slate-900">{dateOpened || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Last Payment</p>
              <p className="dark:text-white text-slate-900">{lastPaymentDate || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">High Balance/Credit</p>
              <p className="dark:text-white text-slate-900">{formatCurrency(account.high_balance_amount || account.high_credit || creditLimit)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">30-Day Late</p>
              <p className={`font-medium ${parseInt(account['30_day_delinquencies'] || account.late_30 || 0) > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                {account['30_day_delinquencies'] || account.late_30 || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">60-Day Late</p>
              <p className={`font-medium ${parseInt(account['60_day_delinquencies'] || account.late_60 || 0) > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                {account['60_day_delinquencies'] || account.late_60 || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">90-Day Late</p>
              <p className={`font-medium ${parseInt(account['90_day_delinquencies'] || account.late_90 || 0) > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {account['90_day_delinquencies'] || account.late_90 || 0}
              </p>
            </div>
          </div>
          {utilization !== null && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-500">Credit Utilization</span>
                <span className={`font-medium ${utilization > 30 ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {utilization.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${utilization > 50 ? 'bg-red-500' : utilization > 30 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(utilization, 100)}%` }}
                />
              </div>
            </div>
          )}
          {(account.remarks || account.comments) && (account.remarks !== 'N/A') && (
            <div className="mt-3 p-2 rounded-lg dark:bg-slate-700/50 bg-slate-100">
              <p className="text-xs text-slate-500">
                <Info className="w-3 h-3 inline mr-1" />
                {account.remarks || account.comments}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Trade Accounts Section - Uses reports.experian.full_feed.trade_accounts
const TradeAccountsSection = ({ tradeAccounts }) => {
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [filter, setFilter] = useState('all');

  // Get accounts from trade_accounts array
  const accounts = Array.isArray(tradeAccounts) ? tradeAccounts : [];

  const filteredAccounts = accounts.filter(acc => {
    if (filter === 'all') return true;
    const status = (acc.account_status || acc.status || '').toLowerCase();
    if (filter === 'open') return status === 'open' || status === 'active';
    if (filter === 'closed') return status === 'closed' || status === 'paid';
    if (filter === 'delinquent') {
      return parseInt(acc['30_day_delinquencies'] || acc.late_30 || 0) > 0 ||
             parseInt(acc['60_day_delinquencies'] || acc.late_60 || 0) > 0;
    }
    return true;
  });

  const toggleAccount = (idx) => {
    setExpandedAccounts(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl overflow-hidden">
      <div className="p-4 border-b dark:border-slate-800 border-slate-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2">
          <Building2 className="w-5 h-5 dark:text-sky-400 text-sky-600" />
          Trade Accounts ({accounts.length})
        </h3>
        <div className="flex items-center gap-2">
          {['all', 'open', 'closed', 'delinquent'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                filter === f
                  ? 'bg-sky-600 text-white'
                  : 'dark:bg-slate-800 bg-slate-100 dark:text-slate-400 text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {filteredAccounts.map((account, idx) => (
          <TradeAccountRow
            key={idx}
            account={account}
            isExpanded={expandedAccounts[idx]}
            onToggle={() => toggleAccount(idx)}
          />
        ))}
        {filteredAccounts.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            {accounts.length === 0 ? 'No trade accounts found' : 'No accounts match the selected filter'}
          </div>
        )}
      </div>
    </div>
  );
};

// Inquiries Section
const InquiriesSection = ({ inquiries }) => {
  const inquiryList = Array.isArray(inquiries) ? inquiries : [];

  const getIndustryColor = (industry) => {
    const i = (industry || '').toLowerCase();
    if (i.includes('mortgage') || i.includes('real estate')) return 'bg-sky-500/20 text-sky-500';
    if (i.includes('auto') || i.includes('vehicle')) return 'bg-violet-500/20 text-violet-500';
    if (i.includes('bank') || i.includes('credit')) return 'bg-emerald-500/20 text-emerald-500';
    if (i.includes('retail')) return 'bg-amber-500/20 text-amber-500';
    return 'bg-slate-500/20 text-slate-500';
  };

  return (
    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
      <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 dark:text-sky-400 text-sky-600" />
        Recent Inquiries ({inquiryList.length})
      </h3>
      <div className="space-y-2">
        {inquiryList.map((inquiry, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50"
          >
            <div className="flex items-center gap-3">
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getIndustryColor(inquiry.industry || inquiry.type)}`}>
                {inquiry.industry || inquiry.type || 'Other'}
              </div>
              <span className="font-medium dark:text-white text-slate-900">
                {inquiry.company || inquiry.creditor || inquiry.subscriber_name || 'Unknown'}
              </span>
            </div>
            <span className="text-sm text-slate-500">{inquiry.date || inquiry.inquiry_date || 'N/A'}</span>
          </div>
        ))}
        {inquiryList.length === 0 && (
          <div className="text-center py-4 text-slate-500 text-sm">No recent inquiries</div>
        )}
      </div>
    </div>
  );
};

// Collections Section
const CollectionsSection = ({ collections }) => {
  const collectionList = Array.isArray(collections) ? collections : [];

  if (collectionList.length === 0) {
    return (
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 dark:text-sky-400 text-sky-600" />
          Collections & Derogatory Items
        </h3>
        <div className="flex items-center justify-center py-8 text-emerald-500">
          <CheckCircle className="w-6 h-6 mr-2" />
          <span className="font-medium">No collections or derogatory items found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
      <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
        <AlertTriangle className="w-5 h-5 text-amber-500" />
        Collections & Derogatory Items ({collectionList.length})
      </h3>
      <div className="space-y-3">
        {collectionList.map((collection, idx) => {
          const status = collection.status || collection.account_status || '';
          const isPaid = status.toLowerCase().includes('paid');

          return (
            <div
              key={idx}
              className={`p-4 rounded-xl border ${
                isPaid
                  ? 'dark:border-emerald-500/30 border-emerald-200 dark:bg-emerald-500/10 bg-emerald-50'
                  : 'dark:border-red-500/30 border-red-200 dark:bg-red-500/10 bg-red-50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-semibold dark:text-white text-slate-900">
                    {collection.company || collection.creditor || collection.agency || 'Unknown'}
                  </p>
                  <p className="text-xs text-slate-500">
                    Original: {collection.original_creditor || 'N/A'}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isPaid ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                }`}>
                  {status || 'Unknown'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Original Amount</p>
                  <p className="font-medium dark:text-white text-slate-900">
                    {formatCurrency(collection.original_amount || collection.amount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Current Balance</p>
                  <p className="font-medium dark:text-white text-slate-900">
                    {formatCurrency(collection.balance || collection.current_balance)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Date</p>
                  <p className="font-medium dark:text-white text-slate-900">
                    {collection.date_paid || collection.date || 'N/A'}
                  </p>
                </div>
              </div>
              {collection.remarks && (
                <p className="mt-2 text-xs text-slate-500 italic">{collection.remarks}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Assets Section - handles nested asset structure
const AssetsSection = ({ assets }) => {
  if (!assets || Object.keys(assets).length === 0) {
    return (
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 dark:text-sky-400 text-sky-600" />
          Assets
        </h3>
        <div className="text-center py-4 text-slate-500 text-sm">No assets reported</div>
      </div>
    );
  }

  const liquidAssets = assets.liquid_assets || {};
  const retirementAccounts = assets.retirement_accounts || {};
  const investmentAccounts = assets.investment_accounts || {};
  const vehicles = assets.vehicles || [];
  const giftFunds = assets.gift_funds || {};
  const proceedsFromSale = assets.proceeds_from_sale || {};

  return (
    <div className="space-y-6">
      {/* Liquid Assets */}
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 dark:text-emerald-400 text-emerald-600" />
          Liquid Assets
          <span className="ml-auto text-emerald-500 font-bold">{formatCurrency(liquidAssets.total_liquid)}</span>
        </h3>
        <div className="space-y-3">
          {liquidAssets.checking_accounts?.map((acc, idx) => (
            <div key={`check-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50 flex items-center justify-between">
              <div>
                <p className="font-medium dark:text-white text-slate-900">{acc.institution}</p>
                <p className="text-xs text-slate-500">{acc.account_type} • {acc.account_number}</p>
              </div>
              <p className="font-semibold dark:text-emerald-400 text-emerald-600">{formatCurrency(acc.current_balance)}</p>
            </div>
          ))}
          {liquidAssets.savings_accounts?.map((acc, idx) => (
            <div key={`save-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50 flex items-center justify-between">
              <div>
                <p className="font-medium dark:text-white text-slate-900">{acc.institution}</p>
                <p className="text-xs text-slate-500">{acc.account_type} • {acc.account_number}</p>
              </div>
              <p className="font-semibold dark:text-emerald-400 text-emerald-600">{formatCurrency(acc.current_balance)}</p>
            </div>
          ))}
          {liquidAssets.money_market_accounts?.map((acc, idx) => (
            <div key={`mm-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50 flex items-center justify-between">
              <div>
                <p className="font-medium dark:text-white text-slate-900">{acc.institution}</p>
                <p className="text-xs text-slate-500">{acc.account_type} • {acc.account_number}</p>
              </div>
              <p className="font-semibold dark:text-emerald-400 text-emerald-600">{formatCurrency(acc.current_balance)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Retirement Accounts */}
      {(retirementAccounts.employer_401k?.length > 0 || retirementAccounts.ira_accounts?.length > 0) && (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 dark:text-sky-400 text-sky-600" />
            Retirement Accounts
            <span className="ml-auto text-sky-500 font-bold">{formatCurrency(retirementAccounts.total_retirement)}</span>
          </h3>
          <div className="space-y-3">
            {retirementAccounts.employer_401k?.map((acc, idx) => (
              <div key={`401k-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium dark:text-white text-slate-900">{acc.institution}</p>
                  <p className="font-semibold dark:text-sky-400 text-sky-600">{formatCurrency(acc.current_balance)}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Vested:</span> <span className="dark:text-white text-slate-900">{formatCurrency(acc.vested_balance)}</span></div>
                  <div><span className="text-slate-500">Available for Loan:</span> <span className="dark:text-white text-slate-900">{formatCurrency(acc.available_for_loan)}</span></div>
                </div>
              </div>
            ))}
            {retirementAccounts.ira_accounts?.map((acc, idx) => (
              <div key={`ira-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50 flex items-center justify-between">
                <div>
                  <p className="font-medium dark:text-white text-slate-900">{acc.institution}</p>
                  <p className="text-xs text-slate-500">{acc.account_type}</p>
                </div>
                <p className="font-semibold dark:text-sky-400 text-sky-600">{formatCurrency(acc.current_balance)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Investment Accounts */}
      {investmentAccounts.brokerage_accounts?.length > 0 && (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 dark:text-violet-400 text-violet-600" />
            Investment Accounts
            <span className="ml-auto text-violet-500 font-bold">{formatCurrency(investmentAccounts.total_investments)}</span>
          </h3>
          <div className="space-y-3">
            {investmentAccounts.brokerage_accounts?.map((acc, idx) => (
              <div key={`invest-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50 flex items-center justify-between">
                <div>
                  <p className="font-medium dark:text-white text-slate-900">{acc.institution}</p>
                  <p className="text-xs text-slate-500">{acc.holdings_summary}</p>
                </div>
                <p className="font-semibold dark:text-violet-400 text-violet-600">{formatCurrency(acc.current_balance)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vehicles */}
      {vehicles.length > 0 && (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <Car className="w-5 h-5 dark:text-amber-400 text-amber-600" />
            Vehicles
          </h3>
          <div className="space-y-3">
            {vehicles.map((vehicle, idx) => (
              <div key={`veh-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium dark:text-white text-slate-900">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                  <p className="font-semibold dark:text-emerald-400 text-emerald-600">{formatCurrency(vehicle.equity)} equity</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Value:</span> <span className="dark:text-white text-slate-900">{formatCurrency(vehicle.estimated_value)}</span></div>
                  <div><span className="text-slate-500">Loan Balance:</span> <span className="dark:text-white text-slate-900">{formatCurrency(vehicle.loan_balance)}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gift Funds */}
      {giftFunds.expected_gifts?.length > 0 && (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 dark:text-pink-400 text-pink-600" />
            Gift Funds
            <span className="ml-auto text-pink-500 font-bold">{formatCurrency(giftFunds.total_gift_funds)}</span>
          </h3>
          <div className="space-y-3">
            {giftFunds.expected_gifts?.map((gift, idx) => (
              <div key={`gift-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium dark:text-white text-slate-900">{gift.donor_name}</p>
                  <p className="font-semibold dark:text-pink-400 text-pink-600">{formatCurrency(gift.amount)}</p>
                </div>
                <p className="text-xs text-slate-500">{gift.donor_relationship} • Gift letter: {gift.gift_letter_received ? 'Received' : 'Pending'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Proceeds from Sale */}
      {proceedsFromSale.net_proceeds && (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <Home className="w-5 h-5 dark:text-cyan-400 text-cyan-600" />
            Proceeds from Property Sale
          </h3>
          <div className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50">
            <p className="text-sm text-slate-500 mb-2">{proceedsFromSale.property_address}</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-500">Sale Price:</span> <span className="dark:text-white text-slate-900 font-medium">{formatCurrency(proceedsFromSale.sale_price)}</span></div>
              <div><span className="text-slate-500">Net Proceeds:</span> <span className="dark:text-emerald-400 text-emerald-600 font-semibold">{formatCurrency(proceedsFromSale.net_proceeds)}</span></div>
              <div><span className="text-slate-500">Current Remaining:</span> <span className="dark:text-sky-400 text-sky-600 font-semibold">{formatCurrency(proceedsFromSale.current_remaining)}</span></div>
              <div><span className="text-slate-500">Sale Date:</span> <span className="dark:text-white text-slate-900">{proceedsFromSale.sale_date}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Liabilities Section - shows debt breakdown
const LiabilitiesSection = ({ liabilities }) => {
  if (!liabilities || Object.keys(liabilities).length === 0) {
    return (
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 dark:text-red-400 text-red-600" />
          Liabilities
        </h3>
        <div className="text-center py-4 text-slate-500 text-sm">No liabilities reported</div>
      </div>
    );
  }

  const revolvingDebt = liabilities.revolving_debt || {};
  const installmentDebt = liabilities.installment_debt || {};
  const collectionsJudgments = liabilities.collections_judgments || {};

  return (
    <div className="space-y-6">
      {/* Revolving Debt - Credit Cards */}
      {revolvingDebt.credit_cards?.length > 0 && (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <CreditCard className="w-5 h-5 dark:text-red-400 text-red-600" />
            Credit Cards
            <span className="ml-auto text-red-500 font-bold">{formatCurrency(revolvingDebt.total_revolving_balance)}</span>
          </h3>
          <div className="space-y-3">
            {revolvingDebt.credit_cards.map((card, idx) => (
              <div key={`cc-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium dark:text-white text-slate-900">{card.creditor}</p>
                  <p className="font-semibold dark:text-red-400 text-red-600">{formatCurrency(card.current_balance)}</p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-slate-500">Limit:</span> <span className="dark:text-white text-slate-900">{formatCurrency(card.credit_limit)}</span></div>
                  <div><span className="text-slate-500">Min Payment:</span> <span className="dark:text-white text-slate-900">{formatCurrency(card.minimum_payment)}</span></div>
                  <div><span className="text-slate-500">APR:</span> <span className="dark:text-white text-slate-900">{card.interest_rate}%</span></div>
                </div>
                {/* Utilization bar */}
                <div className="mt-2">
                  <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        (card.current_balance / card.credit_limit) * 100 > 50 ? 'bg-red-500' :
                        (card.current_balance / card.credit_limit) * 100 > 30 ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min((card.current_balance / card.credit_limit) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t dark:border-slate-700 border-slate-200 flex justify-between text-sm">
            <span className="text-slate-500">Total Min Payment:</span>
            <span className="font-semibold dark:text-white text-slate-900">{formatCurrency(revolvingDebt.total_revolving_minimum_payment)}/mo</span>
          </div>
        </div>
      )}

      {/* Auto Loans */}
      {installmentDebt.auto_loans?.length > 0 && (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <Car className="w-5 h-5 dark:text-amber-400 text-amber-600" />
            Auto Loans
          </h3>
          <div className="space-y-3">
            {installmentDebt.auto_loans.map((loan, idx) => (
              <div key={`auto-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium dark:text-white text-slate-900">{loan.creditor}</p>
                    <p className="text-xs text-slate-500">{loan.collateral}</p>
                  </div>
                  <p className="font-semibold dark:text-amber-400 text-amber-600">{formatCurrency(loan.current_balance)}</p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-slate-500">Original:</span> <span className="dark:text-white text-slate-900">{formatCurrency(loan.original_amount)}</span></div>
                  <div><span className="text-slate-500">Payment:</span> <span className="dark:text-white text-slate-900">{formatCurrency(loan.monthly_payment)}/mo</span></div>
                  <div><span className="text-slate-500">Remaining:</span> <span className="dark:text-white text-slate-900">{loan.months_remaining} months</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Student Loans */}
      {installmentDebt.student_loans?.length > 0 && (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <GraduationCap className="w-5 h-5 dark:text-violet-400 text-violet-600" />
            Student Loans
          </h3>
          <div className="space-y-3">
            {installmentDebt.student_loans.map((loan, idx) => (
              <div key={`student-${idx}`} className="p-3 rounded-lg dark:bg-slate-800/50 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium dark:text-white text-slate-900">{loan.creditor}</p>
                    <p className="text-xs text-slate-500">{loan.loan_type}</p>
                  </div>
                  <p className="font-semibold dark:text-violet-400 text-violet-600">{formatCurrency(loan.current_balance)}</p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-slate-500">Original:</span> <span className="dark:text-white text-slate-900">{formatCurrency(loan.original_amount)}</span></div>
                  <div><span className="text-slate-500">Payment:</span> <span className="dark:text-white text-slate-900">{formatCurrency(loan.monthly_payment)}/mo</span></div>
                  <div><span className="text-slate-500">Rate:</span> <span className="dark:text-white text-slate-900">{loan.interest_rate}%</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collections */}
      {collectionsJudgments.collections?.length > 0 && (
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Collections
          </h3>
          <div className="space-y-3">
            {collectionsJudgments.collections.map((coll, idx) => (
              <div key={`coll-${idx}`} className={`p-3 rounded-lg ${coll.status === 'Paid in Full' ? 'dark:bg-emerald-500/10 bg-emerald-50' : 'dark:bg-red-500/10 bg-red-50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium dark:text-white text-slate-900">{coll.creditor}</p>
                    <p className="text-xs text-slate-500">Original: {coll.original_creditor}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${coll.status === 'Paid in Full' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                    {coll.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Original:</span> <span className="dark:text-white text-slate-900">{formatCurrency(coll.original_amount)}</span></div>
                  <div><span className="text-slate-500">Balance:</span> <span className="dark:text-white text-slate-900">{formatCurrency(coll.current_balance)}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Total Summary */}
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">Liability Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between py-2 border-b dark:border-slate-700 border-slate-200">
            <span className="text-slate-500">Revolving Debt</span>
            <span className="font-medium dark:text-white text-slate-900">{formatCurrency(revolvingDebt.total_revolving_balance)}</span>
          </div>
          <div className="flex justify-between py-2 border-b dark:border-slate-700 border-slate-200">
            <span className="text-slate-500">Installment Debt</span>
            <span className="font-medium dark:text-white text-slate-900">{formatCurrency(installmentDebt.total_installment_balance)}</span>
          </div>
          <div className="flex justify-between py-2 text-lg font-semibold">
            <span className="dark:text-white text-slate-900">Total Liabilities</span>
            <span className="dark:text-red-400 text-red-600">{formatCurrency((revolvingDebt.total_revolving_balance || 0) + (installmentDebt.total_installment_balance || 0))}</span>
          </div>
          <div className="flex justify-between py-2 border-t dark:border-slate-700 border-slate-200">
            <span className="text-slate-500">Total Monthly Payment</span>
            <span className="font-semibold dark:text-amber-400 text-amber-600">{formatCurrency((revolvingDebt.total_revolving_minimum_payment || 0) + (installmentDebt.total_installment_payment || 0))}/mo</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Applicant Info Section
const ApplicantInfoSection = ({ applicant }) => {
  if (!applicant) {
    return (
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
          <User className="w-5 h-5 dark:text-sky-400 text-sky-600" />
          Applicant Information
        </h3>
        <div className="text-center py-4 text-slate-500 text-sm">No applicant information available</div>
      </div>
    );
  }

  const fullName = [
    applicant.first_name || applicant.firstName,
    applicant.middle_name || applicant.middleName,
    applicant.last_name || applicant.lastName
  ].filter(Boolean).join(' ');

  const fullAddress = [
    applicant.address || applicant.street,
    applicant.city,
    applicant.state,
    applicant.zip || applicant.zipCode
  ].filter(Boolean).join(', ');

  return (
    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
      <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
        <User className="w-5 h-5 dark:text-sky-400 text-sky-600" />
        Applicant Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Full Name</p>
              <p className="font-medium dark:text-white text-slate-900">{fullName || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Date of Birth</p>
              <p className="font-medium dark:text-white text-slate-900">
                {applicant.dob || applicant.date_of_birth || applicant.birthDate || 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MapPin className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Address</p>
              <p className="font-medium dark:text-white text-slate-900">{fullAddress || 'N/A'}</p>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Phone className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Phone</p>
              <p className="font-medium dark:text-white text-slate-900">
                {applicant.phone || applicant.phoneNumber || 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Email</p>
              <p className="font-medium dark:text-white text-slate-900">
                {applicant.email || applicant.emailAddress || 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Briefcase className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">SSN (Last 4)</p>
              <p className="font-medium dark:text-white text-slate-900">
                {applicant.ssn_last4 || applicant.ssn?.slice(-4) || '****'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Credit Report Tab Component
const CreditReportTab = ({ caseId }) => {
  const [creditReport, setCreditReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('bureaus');

  const fetchCreditReport = useCallback(async () => {
    if (!caseId) return;

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = `${BASE_URL}/api/credit-reports/${caseId}/latest`;
      console.log('[CreditReportTab] Fetching credit report:', {
        caseId,
        url: apiUrl,
        BASE_URL: BASE_URL || '(empty - using proxy)'
      });

      const response = await fetchWithAuth(apiUrl);

      console.log('[CreditReportTab] Response:', {
        status: response.status,
        ok: response.ok
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[CreditReportTab] Credit report data received:', data);
        // Data is nested: { data: { metadata: {...}, report: {...} }, status: 200 }
        // Extract the report object which contains applicant, summary, assets_and_liabilities
        const reportData = data.data?.report || data.data || data;
        console.log('[CreditReportTab] Extracted report data:', reportData);
        setCreditReport(reportData);
      } else if (response.status === 404) {
        setCreditReport(null);
      } else {
        throw new Error('Failed to fetch credit report');
      }
    } catch (err) {
      console.error('Error fetching credit report:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchCreditReport();
  }, [fetchCreditReport]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin dark:text-sky-400 text-sky-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-lg font-medium dark:text-white text-slate-900 mb-2">Error Loading Credit Report</p>
        <p className="text-slate-500 mb-4">{error}</p>
        <button
          onClick={fetchCreditReport}
          className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  if (!creditReport) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <FileText className="w-16 h-16 text-slate-400 mb-4" />
        <p className="text-lg font-medium dark:text-white text-slate-900 mb-2">No Credit Report Available</p>
        <p className="text-slate-500 mb-4">A credit report has not been pulled for this case yet.</p>
        <button
          onClick={fetchCreditReport}
          className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Check Again
        </button>
      </div>
    );
  }

  // Extract data - creditReport is the report object containing applicant, summary, assets_and_liabilities, reports
  const applicant = creditReport.applicant;
  const summary = creditReport.summary || {};
  const assetsAndLiabilities = creditReport.assets_and_liabilities || {};
  const reports = creditReport.reports || {};

  // Trade accounts from Experian full_feed
  const tradeAccounts = reports.experian?.full_feed?.trade_accounts || [];
  const inquiries = reports.experian?.full_feed?.inquiries || [];
  const collections = reports.experian?.full_feed?.collections || [];

  // Assets and liabilities from assets_and_liabilities section
  const liabilities = assetsAndLiabilities.liabilities || {};
  const assets = assetsAndLiabilities.assets || {};


  return (
    <div className="p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
      {/* Section Navigation */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'bureaus', label: 'Bureaus', icon: BarChart3 },
          { id: 'summary', label: 'Summary', icon: CreditCard },
          { id: 'accounts', label: 'Trade Accounts', icon: Building2 },
          { id: 'assets', label: 'Assets', icon: Shield },
          { id: 'liabilities', label: 'Liabilities', icon: DollarSign },
          { id: 'inquiries', label: 'Inquiries', icon: Clock },
          { id: 'collections', label: 'Collections', icon: AlertTriangle },
          { id: 'applicant', label: 'Applicant', icon: User },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveSection(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeSection === id
                ? 'bg-sky-600 text-white'
                : 'dark:bg-slate-800 bg-slate-100 dark:text-slate-400 text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
        <button
          onClick={fetchCreditReport}
          className="ml-auto p-2 rounded-lg dark:bg-slate-800 bg-slate-100 dark:text-slate-400 text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Content Sections */}
      {activeSection === 'bureaus' && (
        <BureauReportSection reports={reports} summary={summary} />
      )}

      {activeSection === 'summary' && (
        <div className="space-y-6">
          <ScoreSummarySection summary={summary} />
          <DebtSummarySection summary={summary} />
        </div>
      )}

      {activeSection === 'accounts' && (
        <TradeAccountsSection tradeAccounts={tradeAccounts} />
      )}

      {activeSection === 'assets' && (
        <AssetsSection assets={assets} />
      )}

      {activeSection === 'liabilities' && (
        <LiabilitiesSection liabilities={liabilities} />
      )}

      {activeSection === 'inquiries' && (
        <InquiriesSection inquiries={inquiries} />
      )}

      {activeSection === 'collections' && (
        <CollectionsSection collections={collections} />
      )}

      {activeSection === 'applicant' && (
        <ApplicantInfoSection applicant={applicant} />
      )}
    </div>
  );
};

export default CreditReportTab;
